import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
// import { Link, useLocation } from 'react-router-dom'; // 웹빌더에서는 라우터 미사용
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useDispatch, useSelector } from 'react-redux';
import { logoutUser, selectCartState } from '../../../../redux/WithcookieSlice';

import { SearchBar } from '../ui/input/SearchBar';
import { SearchIcon, LoginIcon, CartIcon, UserIcon, LogoutIcon } from '../icons';

gsap.registerPlugin(ScrollTrigger);

/**
 * Menu 컴포넌트 - 헤더 우측 메뉴 (데스크톱 전용)
 *
 * 구조:
 * - 검색, 로그인, 장바구니 버튼으로 구성
 * - 검색 버튼 클릭 시 전체화면 오버레이 검색창
 * - Portal을 사용한 모달형 검색 인터페이스
 *
 * 상태 관리:
 * - isSearchBarOpen: 검색창 오버레이 열림/닫힘 상태
 * - isMounted: DOM 마운트 상태 (Portal 사용을 위함)
 *
 * 애니메이션:
 * - GSAP로 버튼들 fade-in 효과
 * - 검색 오버레이 fade-in/out 애니메이션
 * - ScrollTrigger로 스크롤 인터랙션
 */
interface MenuProps {
    isLoggedIn?: boolean;
}

export function Menu({ isLoggedIn = false }: MenuProps) {
    const [isSearchBarOpen, setIsSearchBarOpen] = useState<boolean>(false);
    const [isMounted, setIsMounted] = useState<boolean>(false);
    const dispatch = useDispatch() as any;
    
    // Redux에서 장바구니 상태 가져오기
    const { cartItems } = useSelector(selectCartState);
    
    // Redux에서 테마 색상 가져오기
    const withcookieData = useSelector((state: any) => state.withcookie?.withcookieData);
    const themeColor = withcookieData?.skin?.theme?.colorset?.primary || '#89a1be';

    // 웹빌더 환경에서는 location 대신 window.location 사용

    function handleToggleSearchBar() {
        setIsSearchBarOpen(!isSearchBarOpen);
    }

    // 로그아웃 처리 함수
    const handleLogout = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        dispatch(logoutUser()).then(() => {
            // 로그아웃 성공 후 홈 페이지로 이동
            window.location.href = '/';
        });
    };

    // DOM이 마운트되면 상태를 변경
    useEffect(() => {
        setIsMounted(true);
    }, []);

    // URL에 keyword가 있으면 SearchBar를 자동으로 열기
    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        if (searchParams.has('keyword')) {
            setIsSearchBarOpen(true);
        }
    }, []);

    useGSAP(() => {
        // 브레이크 포인트 fade-in 애니메이션
        const searchBtn = document.querySelector('.searchBtn');
        const userBtn = document.querySelector('.userBtn');
        const loginBtn = document.querySelector('.loginBtn');
        const cartBtn = document.querySelector('.cartBtn');

        if (searchBtn && loginBtn && cartBtn && userBtn) {
            // GSAP 존재 여부 체크
            if (typeof gsap !== 'undefined') {
                const checkAndAnimate = () => {
                    if (window.innerWidth >= 1024) { // 64rem = 1024px
                        gsap.to([searchBtn, userBtn, loginBtn], {
                            opacity: 1,
                            duration: 0.9,
                            ease: 'power2.out',
                        });
                    } else {
                        gsap.to([searchBtn, userBtn, loginBtn], {
                            opacity: 0,
                            duration: 0.9,
                            ease: 'power2.out',
                        });
                    }
                };
                
                // 초기 실행
                checkAndAnimate();
                
                // 리사이즈 이벤트에도 대응
                window.addEventListener('resize', checkAndAnimate);
                
                // 장바구니 버튼은 항상 보이도록
                gsap.to(cartBtn, {
                    opacity: 1,
                    duration: 0.9,
                    ease: 'power2.out',
                });
                
                return () => {
                    window.removeEventListener('resize', checkAndAnimate);
                };
            } else {
                // GSAP이 없을 때는 직접 opacity 설정
                if (window.innerWidth >= 1024) {
                    (searchBtn as HTMLElement).style.opacity = '1';
                    (userBtn as HTMLElement).style.opacity = '1';
                    (loginBtn as HTMLElement).style.opacity = '1';
                } else {
                    (searchBtn as HTMLElement).style.opacity = '0';
                    (userBtn as HTMLElement).style.opacity = '0';
                    (loginBtn as HTMLElement).style.opacity = '0';
                }
                // 장바구니는 항상 보이도록
                (cartBtn as HTMLElement).style.opacity = '1';
            }
        }
    }, []);

    useGSAP(() => {
        const searchBarContainer = document.querySelector('#header .searchBarContainer');

        if (!searchBarContainer) return;

        if (isSearchBarOpen) {
            gsap.to(searchBarContainer, {
                opacity: 1,
                visibility: 'visible',
                height: '140px',
                duration: 0.5,
                ease: 'power2.out',
            });
        } else {
            gsap.timeline()
                .to(searchBarContainer, {
                    opacity: 0,
                    height: 0,
                    duration: 0.5,
                    ease: 'power2.out',
                })
                .set(
                    searchBarContainer,
                    {
                        visibility: 'hidden',
                    },
                    '-=0.35'
                );
        }
    }, [isSearchBarOpen]);

    return (
        <nav
            id="menu"
            className="lg:w-[300px]"
        >
            <ul className="flex items-center justify-end gap-x-3.5">
                {/*<li className="searchBtn lg:flex hidden items-center justify-center">*/}
                {/*    <button*/}
                {/*        className="block"*/}
                {/*        aria-label="검색"*/}
                {/*        type="button"*/}
                {/*        onClick={handleToggleSearchBar}*/}
                {/*    >*/}
                {/*        <SearchIcon />*/}
                {/*    </button>*/}
                {/*</li>*/}
                {isLoggedIn && (
                    <li className="userBtn lg:flex hidden items-center justify-center">
                        <button
                            className="block"
                            aria-label="마이페이지"
                            type="button"
                        >
                            <a href="/mydata">
                                <UserIcon />
                            </a>
                        </button>
                    </li>
                )}
                <li className="loginBtn lg:flex hidden items-center justify-center">
                    {isLoggedIn ? (
                        <button
                            className="block"
                            aria-label="로그아웃"
                            type="button"
                        >
                            <a href="/logout" onClick={handleLogout}>
                                <LogoutIcon />
                            </a>
                        </button>
                    ) : (
                        <button
                            className="block"
                            aria-label="로그인"
                            type="button"
                        >
                            <a href="/login">
                                <LoginIcon />
                            </a>
                        </button>
                    )}
                </li>
                <li className="cartBtn flex items-center justify-center">
                    <button
                        className="block lg:py-0 py-3.5 md:py-5"
                        aria-label="장바구니"
                        type="button"
                    >
                        <a href="/cart">
                            <CartIcon />
                        </a>
                    </button>
                    <div className="rounded-full w-6 h-6 flex items-center justify-center ml-1" style={{ backgroundColor: themeColor }}>
                        <span className="text-sm text-white font-bold">
                            {cartItems && cartItems.length ? cartItems.length : 0}
                        </span>
                    </div>
                </li>
            </ul>

            {/* DOM이 마운트되면 Portal 렌더링 */}
            {/* 헤더에서 props drilling을 피하기 위해 portal 사용 */}
            {isMounted &&
                createPortal(
                    <div className="searchBarContainer hidden invisible lg:flex w-full opacity-0 h-0 items-center justify-center border-t border-black/10 bg-white">
                        <SearchBar tailwind="lg:w-full lg:max-w-[680px]" />
                    </div>,
                    document.querySelector('#header') || document.body
                )}
        </nav>
    );
}